<template>
  <div>
    <CraftingRecipeStatus :ready-time="readyTime" />
    <CraftingNftZoomImage v-if="iconUrl" v-model="isShowNftZoomModal" :src="iconUrl" :nft-name="nftName" />
    <el-skeleton :loading="recipeRequestStatus !== 'success'" animated>
      <template #template>
        <div class="tw:px-4">
          <el-skeleton-item style="width: 100%; height: 158px" />
        </div>
      </template>
      <template #default>
        <div class="crafting-recipe">
          <ul class="crafting-materials">
            <CraftingMaterial
              v-for="(item, i) of recipeToCraft?.ingredients"
              :key="i"
              :ingredient="item"
              :produce-amount="produceAmount"
            />
          </ul>
          <div class="crafting-arrow">→</div>
          <div v-for="finalProduct of recipeToCraft?.products" :key="finalProduct.productId" class="crafting-crafted">
            <div class="crafting-nft-content" @click="openZoom">
              <img v-if="isNft && isRandomNft" class="random-nft" src="/img/icons/random-nft.svg" alt="nft-random" />
              <img v-if="isNft" class="zoom-icon" src="/img/crafting/zoomNftIcon.svg" alt="zoom" />
              <div class="crafting-img-wrapper">
                <img
                  class="crafted-item-img"
                  :src="$getIconToken(finalProduct, BigNumber(recipeId).toNumber())"
                  alt=""
                />
              </div>
              <p v-if="isNft" class="valuable">
                {{ $t(`core${Formatters.text.firstLetterUppercase($getProductUniqueness(finalProduct))}`) }}
              </p>
              <p v-if="!isNft" :style="{ fontSize: $i18n.locale === 'ru' ? '16px' : '18px' }">
                <span v-if="new BigNumber(finalProduct.quantityMin).eq(finalProduct.quantityMax)">
                  {{ new BigNumber(finalProduct.quantityMax).multipliedBy(produceAmount || 0) }}
                </span>
                <span v-else>
                  {{ new BigNumber(finalProduct.quantityMin).decimalPlaces(1).multipliedBy(produceAmount || 0) }}-{{
                    new BigNumber(finalProduct.quantityMax).decimalPlaces(1).multipliedBy(produceAmount || 0)
                  }}
                </span>
              </p>
            </div>
            <div class="token-info" @click="() => handleTokenInfoClick(finalProduct)">
              <div class="token-info-walletImg">
                <img src="/img/crafting/wallet.svg" alt="wallet" />
              </div>
              <div class="token-info-tokenImg">
                <img :src="$getIconToken(finalProduct, BigNumber(recipeId).toNumber())" alt="" />
                <el-skeleton
                  :loading="isNft ? isNftAmountLoading : isTokenAmountLoading"
                  animated
                  style="max-height: 17px"
                >
                  <template #template>
                    <el-skeleton-item style="width: 20px; height: 18px" />
                  </template>

                  <template #default>
                    <span v-if="isNft">
                      {{ nftAmount?.toLocaleString('ru-RU') }}
                    </span>
                    <span v-else-if="tokenAmount">
                      {{ Number(tokenAmount[finalProduct.tokenAddress]).toLocaleString('ru-RU') }}
                    </span>
                  </template>
                </el-skeleton>
              </div>
            </div>
          </div>
        </div>
      </template>
    </el-skeleton>

    <div class="crafting-buttons">
      <div class="crafting-buttons-wrapper">
        <el-input-number
          v-model="produceAmount"
          :step="1"
          :step-strictly="true"
          :validate-event="false"
          :min="MINIMUM_CRAFTING_RECIPES"
          :disabled="!maxAmountAvailableForCraft"
          :max="maxAmountAvailableForCraft || 1"
          :value-on-clear="maxAmountAvailableForCraft || 1"
        />
        <!-- TODO: убрать important -->
        <button
          class="tw:p-0! tw:sm:px-4! btn-outline btn-max"
          type="button"
          :disabled="isItemCraftingInjected || (maxAmountAvailableForCraft || 0) <= 0"
          @click.stop="setMaxProduceItem"
        >
          {{ $t('craftingMax') }} ({{ maxAmountAvailableForCraft }})
        </button>
      </div>

      <button
        class="btn-primary"
        :disabled="
          !address ||
          isItemCraftingInjected ||
          (maxAmountAvailableForCraft || 0) <= 0 ||
          produceAmount > (maxAmountAvailableForCraft || 0) ||
          !isEnoughTokens
        "
        @click="handleToggleConfirmationModalInjected && handleToggleConfirmationModalInjected()"
      >
        {{ $t('craftingProduce') }}
      </button>
    </div>

    <TransferTokenModal v-model="showTransferTokenModal" :token="transferToken" />
  </div>
</template>

<script setup lang="ts">
import { useWeb3ModalAccount } from '@web3modal/ethers/vue';
import { BigNumber } from 'bignumber.js';
import { Erc20Token__factory, ERC721__factory } from 'public/abi/evm/types';
import CraftingRecipeStatus from '~/components/Crafting/CraftingRecipeStatus.vue';
import tokens from '~/public/img/tokens';
import type { ITokenModel, StorageItem } from '~/types/apiService';
import { TransferTokenModal } from '~/widgets/wallet';
import { Formatters } from '~/utils/formatters/formatters';
import type { RecipeProduct, RecipeProps } from '~/types/crafting';
import { MINIMUM_CRAFTING_RECIPES } from '~/utils/constants';

const props = defineProps<{
  recipeId: string;
  nftName: string;
}>();
const { isMobile } = useDevice();
const { address } = useWeb3ModalAccount();
const { getProvider } = useProvider();
const store = useMainStore();

const nftAmount = ref<number | null>(null);
const tokenAmount = ref<Record<string, string> | null>(null);
const isShowNftZoomModal = ref(false);
const iconUrl = ref<string | null>(null);
const isTokenAmountLoading = ref(false);
const isNftAmountLoading = ref(false);
const showTransferTokenModal = ref(false);
const transferToken = ref<StorageItem | null>(null);

const isItemCraftingInjected = inject<boolean>('isItemCrafting');
const recipeRequestStatus = inject<Ref<string>>('recipeRequestStatus');
const handleToggleConfirmationModalInjected = inject<() => void>('handleToggleConfirmationModal');
const setProductMultiplierInjected = inject<(multiplier: number) => void>('setProductMultiplier');
const queueTimeInjected = inject<Ref<number>>('queueTime');
const updateQueueTimeInjected = inject<() => void>('updateQueueTime');
const openStorage = inject<(() => void) | null>('openStorageClickHandler', null);

const { data: recipeToCraft } = useNuxtData<RecipeProps>('recipes/recipe');
const { data: tokensData } = useNuxtData<ITokenModel[]>('all-tokens');
const { $getIconToken } = useNuxtApp();

const maxAmountAvailableForCraft = inject<Ref<number>>('maxAmountAvailableForCraft');
const isEnoughTokens = inject<Ref<boolean>>('isEnoughTokens');
const openStorageByMobile = inject<(() => void) | null>('openStorageByMobile', null);
const openStorageByMobileFromDistricts = inject<(() => void) | null>('openStorageByMobileFromDistricts', null);
const openZoom = () => {
  if (!isNft.value) return;

  getTokenImg();
  isShowNftZoomModal.value = true;
};

const getTokenImg = () => {
  if (recipeToCraft?.value) {
    const img = $getIconToken(recipeToCraft?.value?.products[0], BigNumber(props.recipeId || 0).toNumber());
    iconUrl.value = img;
  }
};

const handleTokenInfoClick = (finalProduct: RecipeProduct) => {
  if (isNft.value) {
    if (isMobile) {
      openStorageByMobile?.();
      openStorageByMobileFromDistricts?.();
      return;
    }

    openStorage?.();
    return;
  }

  openTransferTokenModal(finalProduct);
};

const openTransferTokenModal = async (finalProduct: RecipeProduct) => {
  if (!tokensData.value || !address.value) return;
  if (!finalProduct.tokenAddress) return;

  const tokenModel = tokensData.value.find(
    (token) => token.tokenContractAddress.toLowerCase() === finalProduct.tokenAddress?.toLowerCase()
  );

  if (!tokenModel) return;

  try {
    const factory = Erc20Token__factory.connect(tokenModel.tokenContractAddress, getProvider());
    const [rawBalance, rawDecimals] = await Promise.all([factory.balanceOf(address.value), factory.decimals()]);

    const decimals = Number(rawDecimals);
    const bn = new BigNumber(rawBalance.toString());

    transferToken.value = {
      ...tokenModel,
      balance: bn.div(new BigNumber(10).pow(decimals)).toString(),
      shared: '0',
      earnedBalance: '0'
    };

    showTransferTokenModal.value = true;
  } catch (e) {
    // balance fetch failed, do not open modal to avoid incorrect state
    console.error('Failed to fetch token balance for TransferTokenModal in crafting', e);
  }
};

const readyTime = computed(() => {
  if (!queueTimeInjected) {
    return 0;
  }

  return BigNumber(queueTimeInjected.value)
    .plus(
      BigNumber(recipeToCraft.value?.duration || 0)
        .multipliedBy(produceAmount.value)
        .multipliedBy(1000)
    )
    .toNumber();
});

store.$onAction(async ({ name }) => {
  if (name !== 'updateVersion') {
    return;
  }

  setTimeout(async () => {
    await refreshNuxtData('recipes/in-queue');
  }, 2000);
});

const isNft = computed(() => {
  if (!recipeToCraft) return false;

  return (
    recipeToCraft.value &&
    recipeToCraft.value.products.every(
      (product: RecipeProduct) => product.nftValueMin !== null && product.nftValueMax !== null
    )
  );
});

const nftProduct = computed(() => recipeToCraft.value?.products?.[0]);

const produceAmount = ref(1);

watch(produceAmount, (newValue) => {
  setProductMultiplierInjected && setProductMultiplierInjected(newValue);
});

watch(
  () => props.recipeId,
  async () => {
    await refreshNuxtData('recipes/in-queue');

    produceAmount.value = 1;

    updateQueueTimeInjected && updateQueueTimeInjected();
  }
);

const fetchNftAmount = async () => {
  isNftAmountLoading.value = true;
  try {
    const contractAddress = nftProduct.value?.tokenAddress;
    const userAddress = address.value;

    if (!contractAddress || !userAddress) return;

    const tokenIds = await ERC721__factory.connect(contractAddress, getProvider()).getTokenIds(userAddress);
    nftAmount.value = tokenIds.length;
  } finally {
    isNftAmountLoading.value = false;
  }
};

const fetchNonNftAmount = async () => {
  isTokenAmountLoading.value = true;
  try {
    const userAddress = address.value;
    const products = recipeToCraft.value?.products;

    if (!userAddress || !products || products.length === 0) return;

    const result: Record<string, string> = {};

    for (const product of products) {
      const contractAddress = product.tokenAddress;
      if (!contractAddress) continue;

      const factory = Erc20Token__factory.connect(contractAddress, getProvider());

      const rawBalance = await factory.balanceOf(userAddress);

      const rawDecimals = await factory.decimals();

      const decimals = Number(rawDecimals);

      const bn = new BigNumber(rawBalance.toString());

      result[contractAddress] = bn.div(new BigNumber(10).pow(decimals)).integerValue(BigNumber.ROUND_FLOOR).toString();
    }

    tokenAmount.value = result;
  } finally {
    isTokenAmountLoading.value = false;
  }
};

const isRandomNft = computed(() => {
  if (!isNft.value) return;

  const nftAddress = recipeToCraft.value?.products[0].tokenAddress;
  const nftKey = tokensData.value?.find((item) => item.tokenContractAddress === nftAddress)?.key;

  if (!nftKey) return false;

  const tokenValue = tokens[nftKey as keyof typeof tokens];

  if (Array.isArray(tokenValue)) return false;

  if (nftKey.toLowerCase().includes('ship') && Array.isArray(tokens.ships)) {
    return false;
  }

  return true;
});

watch(
  () => nftProduct.value?.tokenAddress,
  async () => {
    if (isNft.value) {
      await fetchNftAmount();
    }
  },
  { immediate: true }
);

watch(
  () => recipeToCraft.value?.products,
  async (newVal) => {
    if (newVal && newVal.length > 0 && !isNft.value) {
      await fetchNonNftAmount();
    }
  },
  { immediate: true }
);

const setMaxProduceItem = () => {
  if (!maxAmountAvailableForCraft) {
    return;
  }
  produceAmount.value = maxAmountAvailableForCraft.value;
};
</script>

<style scoped lang="scss">
.crafting-crafted {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: stretch;
  width: fit-content;
  max-width: 100%;
  flex-direction: column;
  padding: 0;
  cursor: pointer;

  p {
    margin-top: 5px;
    margin-bottom: 0;
    text-align: center;
    width: 100%;
    font-weight: 700;
    font-size: 18px;
    line-height: 1rem;
    position: relative;
    top: -10px;
    color: #fff;
    white-space: nowrap;
  }

  .valuable {
    color: #ffe604;
    font-family: Eczar, sans-serif;
    font-size: 1.2rem;
    padding-top: 6px;
    text-transform: capitalize;
  }
}

.crafting-buttons {
  &-wrapper {
    display: flex;
    gap: 10px;

    @media screen and (max-width: 479px) {
      align-items: center;
    }

    @media screen and (max-width: 900px) {
      width: 100%;
    }
  }

  .amount {
    height: 50px;
  }

  .produce {
    padding: 10px 40px;

    @media screen and (max-width: 479px) {
      min-height: 52px;
    }
  }
}

.crafting-nft-content {
  text-align: center;
  padding: 0 16px;
}

.crafting-img-wrapper {
  display: inline-block;
}

.random-nft,
.zoom-icon {
  position: absolute;
}

.random-nft {
  height: 20px;
  width: 19px;
  top: 37px;
  right: 10px;
}

.zoom-icon {
  width: 20px;
  height: 20px;
  top: 10px;
  right: 10px;
}

.crafted-item-img {
  display: block;
  max-width: 100%;
  padding-top: 3px;
  height: 70px;
  border-radius: 7px;
}

.btn-max {
  border-radius: 12px;
  font-size: 18px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  height: 50px;
}

.crafting-info-duration-skeleton {
  height: 66px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.token-info {
  height: 32px;
  background: radial-gradient(59.02% 71.08% at 84.01% 85.82%, #040f14 0%, #081b25 100%);
  display: flex;
  align-items: center;
  padding-right: 10px;
  width: 100%;
  border-radius: 0 0 12px 12px;

  @media screen and (max-width: 479px) {
    padding-right: 30px;
  }

  &:hover {
    background: radial-gradient(68.4% 92.19% at 50% 92.19%, rgba(0, 131, 173, 0.4) 0%, rgba(0, 0, 0, 0) 100%),
      radial-gradient(59.02% 71.08% at 84.01% 85.82%, #040f14 0%, #081b25 100%);
  }

  &-walletImg,
  &-tokenImg {
    height: 100%;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &-walletImg {
    flex: 0 0 40px;
    position: relative;
    margin-right: 10px;

    &::after {
      content: '';
      position: absolute;
      top: 9px;
      bottom: 9px;
      right: 0;
      width: 1px;
      background-color: #1e353e;
      pointer-events: none;
    }

    img {
      width: 18px;
      height: 14px;
    }
  }

  &-tokenImg {
    flex: 1;

    img {
      width: 24px;
      height: 24px;
      margin-right: 7px;
      border-radius: 6px;
    }

    span {
      font-family: Eczar, sans-serif;
      font-weight: 600;
      font-size: 16px;
      color: #ffffff80;
    }
  }
}
</style>

<style lang="scss">
.crafting-buttons-wrapper .el-input-number {
  width: 130px;
  border: 1px solid #fff;
  border-radius: 12px;
  font-family: Eczar, sans-serif;
  font-weight: 400;

  &__decrease,
  &__increase {
    width: 32px;
    color: var(--main-text-color);
    font-size: 20px;
    font-weight: 700;
    background: linear-gradient(180deg, #0083ad 0%, #00354f 100%);
    top: 0;
    bottom: 0;
  }

  &__decrease {
    border-radius: 12px 0 0 12px;
    left: 0;
  }

  &__increase {
    border-radius: 0 12px 12px 0;
    right: 0;
  }

  .el-input {
    font-size: 18px;
    line-height: 1;
    background: radial-gradient(59.02% 71.08% at 84.01% 85.82%, #040f14 0%, #081b25 100%);
    overflow: hidden;
    border-radius: 12px;

    &__inner {
      --el-input-inner-height: 32px;
    }

    &__wrapper {
      border-radius: 12px;
      box-shadow: none;
      padding: 6px 11px;
    }
  }
}
</style>
